import React, { useState, useEffect, useCallback } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useAuthStore } from "../../stores/auth-store";
import { companyProductionRecordUseCase, companyRouterUseCase, companyMachineUseCase } from "../../../di/container";
import type { ProductionRecord, ProductionRecordFilters } from "../../../domain/types/production-record";
import { ProductionRecordEntity } from "../../../domain/types/production-record";
import type { PageResponse } from "../../../shared/types/common";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  Clock,
  ExternalLink,
} from "lucide-react";

const PAGE_SIZE = 20;

const ProductionRecordManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  // 상태
  const [records, setRecords] = useState<ProductionRecordEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 필터 상태
  const [filters, setFilters] = useState<ProductionRecordFilters>({
    routerId: undefined,
    machineId: undefined,
    startDate: null,
    endDate: null,
    hasOrder: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  // 라우터/장비 옵션
  const [routers, setRouters] = useState<{ id: number; name: string }[]>([]);
  const [machines, setMachines] = useState<{ id: number; name: string; routerId: number }[]>([]);

  // 라우터/장비 목록 조회
  useEffect(() => {
    const fetchOptions = async () => {
      if (!companyId) return;
      try {
        const routerList = await companyRouterUseCase.getRouters(companyId);
        if (Array.isArray(routerList)) {
          setRouters(
            routerList.map((r: any) => ({
              id: r.id,
              name: r.routerName || `라우터 ${r.id}`,
            }))
          );
        }

        const routerMachinesList = await companyMachineUseCase.getMachineDetailsByRouter(companyId);
        // flatMap을 사용하여 모든 라우터의 장비를 평면화
        if (Array.isArray(routerMachinesList)) {
          const allMachines = routerMachinesList.flatMap((rm) =>
            (rm.machines ?? []).map((m) => ({
              id: m.machineId,
              name: m.displayName || m.modelName || `장비 ${m.machineId}`,
              routerId: rm.router?.id ?? 0,
            }))
          );
          setMachines(allMachines);
        }
      } catch (err) {
        console.error("옵션 로드 실패:", err);
      }
    };
    fetchOptions();
  }, [companyId]);

  // 생산 실적 조회
  const fetchRecords = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await companyProductionRecordUseCase.getProductionRecords({
        companyId,
        routerId: filters.routerId,
        machineId: filters.machineId,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        hasOrder: filters.hasOrder ?? undefined,
        page,
        size: PAGE_SIZE,
      });

      // API 응답은 items 또는 content를 가질 수 있음
      const items = (response as any).items ?? (response as any).content ?? [];
      setRecords(items.map((r: any) => new ProductionRecordEntity(r)));
      setTotalPages(response.totalPages ?? 0);
      setTotalElements(response.totalElements ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "데이터 조회 실패");
    } finally {
      setIsLoading(false);
    }
  }, [companyId, filters, page]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // 필터 변경 시 페이지 초기화
  const handleFilterChange = (newFilters: Partial<ProductionRecordFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  };

  // 필터 초기화
  const handleResetFilters = () => {
    setFilters({
      routerId: undefined,
      machineId: undefined,
      startDate: null,
      endDate: null,
      hasOrder: null,
    });
    setPage(0);
  };

  // 주문 상세 페이지로 이동
  const handleOrderClick = (orderId: number) => {
    navigate(`/company/${companyId}/orders?orderId=${orderId}`);
  };

  // 상태 뱃지 스타일
  const getStatusBadgeStyle = (status: ProductionRecord["status"]) => {
    const styles: Record<string, string> = {
      PENDING: "bg-gray-100 text-gray-600",
      PENDING_HELD: "bg-yellow-100 text-yellow-700",
      PROCESSING: "bg-blue-100 text-blue-700",
      PROCESSING_STOPPED: "bg-orange-100 text-orange-700",
      CANCELED: "bg-red-100 text-red-600",
      ABORTED: "bg-red-100 text-red-600",
      COMPLETED: "bg-green-100 text-green-700",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  // 라우터 필터에 따른 장비 목록 필터링
  const filteredMachines = filters.routerId
    ? machines.filter((m) => m.routerId === filters.routerId)
    : machines;

  if (!companyId) {
    return (
      <CompanyAdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">회사 정보를 찾을 수 없습니다.</p>
        </div>
      </CompanyAdminLayout>
    );
  }

  return (
    <CompanyAdminLayout>
      <div className="p-6">
        {/* 헤더 */}
        <div className="flex justify-between items-start mb-6">
          <div className="text-left">
            <h1 className="text-[22px] font-bold text-[#222] text-left">생산 실적 관리</h1>
            <p className="text-[13px] text-[#888] mt-1 text-left">
              장비의 작업 기록을 조회하고 주문과의 연결 상태를 확인합니다.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                showFilters
                  ? "bg-[#E8EFFF] border-[#4285F4] text-[#4285F4]"
                  : "bg-white border-[#e0e0e0] text-[#555] hover:bg-[#f5f5f5]"
              }`}
            >
              <Filter size={16} />
              필터
            </button>
            <button
              onClick={fetchRecords}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#222] text-white hover:bg-[#333] transition-colors"
            >
              <RefreshCw size={16} />
              새로고침
            </button>
          </div>
        </div>

        {/* 필터 패널 */}
        {showFilters && (
          <div className="bg-white rounded-[12px] border border-[#e0e0e0] p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* 라우터 필터 */}
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1 text-left">
                  라우터
                </label>
                <select
                  value={filters.routerId || ""}
                  onChange={(e) =>
                    handleFilterChange({
                      routerId: e.target.value ? Number(e.target.value) : undefined,
                      machineId: undefined, // 라우터 변경 시 장비 초기화
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
                >
                  <option value="">전체</option>
                  {routers.map((router) => (
                    <option key={router.id} value={router.id}>
                      {router.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 장비 필터 */}
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1 text-left">
                  장비
                </label>
                <select
                  value={filters.machineId || ""}
                  onChange={(e) =>
                    handleFilterChange({
                      machineId: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
                >
                  <option value="">전체</option>
                  {filteredMachines.map((machine) => (
                    <option key={machine.id} value={machine.id}>
                      {machine.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 시작일 필터 */}
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1 text-left">
                  시작일
                </label>
                <input
                  type="date"
                  value={filters.startDate?.toISOString().split("T")[0] || ""}
                  onChange={(e) =>
                    handleFilterChange({
                      startDate: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
                />
              </div>

              {/* 종료일 필터 */}
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1 text-left">
                  종료일
                </label>
                <input
                  type="date"
                  value={filters.endDate?.toISOString().split("T")[0] || ""}
                  onChange={(e) =>
                    handleFilterChange({
                      endDate: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
                />
              </div>

              {/* 주문 연결 여부 필터 */}
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1 text-left">
                  주문 연결
                </label>
                <select
                  value={filters.hasOrder == null ? "" : filters.hasOrder.toString()}
                  onChange={(e) =>
                    handleFilterChange({
                      hasOrder:
                        e.target.value === ""
                          ? null
                          : e.target.value === "true",
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e0e0e0] rounded-[8px] text-[14px] focus:border-[#4285F4] focus:outline-none"
                >
                  <option value="">전체</option>
                  <option value="true">연결됨</option>
                  <option value="false">미연결</option>
                </select>
              </div>
            </div>

            {/* 필터 초기화 버튼 */}
            <div className="flex justify-end mt-4">
              <button
                onClick={handleResetFilters}
                className="text-[13px] text-[#888] hover:text-[#555] transition-colors"
              >
                필터 초기화
              </button>
            </div>
          </div>
        )}

        {/* 로딩 상태 */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#4285F4]" size={32} />
          </div>
        )}

        {/* 에러 상태 */}
        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* 데이터 테이블 */}
        {!isLoading && !error && (
          <>
            <div className="bg-white rounded-[12px] border border-[#e0e0e0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#fafafa] border-b border-[#eee]">
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        라우터
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        장비
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        작업 파일명
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        시작 시간
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        완료 시간
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        상태
                      </th>
                      <th className="text-left px-4 py-3 text-[12px] font-medium text-[#555]">
                        연결된 주문
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-[#888]">
                          생산 실적이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      records.map((record) => (
                        <tr
                          key={record.id}
                          className="border-b border-[#eee] hover:bg-[#fafafa] transition-colors"
                        >
                          <td className="px-4 py-3 text-[14px] text-[#555]">
                            {record.routerName || "-"}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#555]">
                            {record.machineName || "-"}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#222]">
                            <span className="max-w-[200px] truncate block" title={record.jobName || undefined}>
                              {record.getDisplayJobName()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[13px] text-[#555]">
                            <div className="flex items-center gap-1">
                              <Clock size={14} className="text-[#888]" />
                              {new Date(record.startTime).toLocaleString("ko-KR")}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[13px] text-[#555]">
                            {record.completeTime ? (
                              <div className="flex items-center gap-1">
                                <Clock size={14} className="text-[#888]" />
                                {new Date(record.completeTime).toLocaleString("ko-KR")}
                              </div>
                            ) : (
                              <span className="text-[#888]">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-1 rounded-[4px] text-[11px] font-medium ${getStatusBadgeStyle(
                                record.status
                              )}`}
                            >
                              {record.getStatusLabel()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {record.hasLinkedOrder() ? (
                              <button
                                onClick={() => handleOrderClick(record.orderId!)}
                                className="flex items-center gap-1 text-[#4285F4] hover:text-[#3367D6] transition-colors"
                              >
                                <LinkIcon size={14} />
                                <span className="text-[13px]">
                                  {record.orderNumber || record.orderName}
                                </span>
                                <ExternalLink size={12} />
                              </button>
                            ) : (
                              <span className="text-[#888] text-[13px]">미연결</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-[13px] text-[#888]">
                  총 {totalElements}건 중 {page * PAGE_SIZE + 1}-
                  {Math.min((page + 1) * PAGE_SIZE, totalElements)}건
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-2 rounded-lg border border-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#f5f5f5] transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-[14px] text-[#555] px-4">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-2 rounded-lg border border-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#f5f5f5] transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default ProductionRecordManagement;
